import { EventVisibility } from '../types/enums/event-visibility.enum'
import { eventPeriod, eventPhase, EventTimes } from './event-edit-lock'

/**
 * What a series edit may change: everything "Upraviť udalosť" offers except the time and the activity, which stay per
 * termín (docs/events.md, _Editing a series_).
 */
export const SERIES_EDIT_FIELDS = [
  'name',
  'placeId',
  'employeeIds',
  'capacity',
  'price',
  'color',
  'visibility',
  'description',
  'cancellationDeadline',
  'allowCustomerNote',
] as const
export type SeriesEditField = (typeof SERIES_EDIT_FIELDS)[number]

/** A termín's values in those fields, in one shape for the form, `ManagedEvent` and `EventEntity`. */
export interface SeriesEditValues {
  name: string
  placeId: string | null
  employeeIds: readonly string[]
  /** `0` is uncapped. */
  capacity: number
  /** EUR; `null` or `0` is no price. */
  price: number | null
  /** A palette hex, `''` for the activity's colour. */
  color: string
  visibility: readonly EventVisibility[]
  /** HTML, `''` for none. */
  description: string
  cancellationDeadline: number
  allowCustomerNote: boolean
}

/** One termín of the series, as the rule reads it. */
export interface SeriesEditOccurrence extends EventTimes, SeriesEditValues {
  id: string
  cancelledAt: Date | string | null
}

const sameSet = (a: readonly string[], b: readonly string[]) => {
  const other = new Set(b)
  const own = new Set(a)
  return own.size === other.size && [...own].every((value) => other.has(value))
}
const same = <T>(a: T, b: T) => a === b

type Comparers = { [F in SeriesEditField]: (a: SeriesEditValues[F], b: SeriesEditValues[F]) => boolean }

/** Values that read the same to a manager are the same: employees as a set, no price as none, a hex in any case. */
const COMPARERS: Comparers = {
  name: (a, b) => a.trim() === b.trim(),
  placeId: (a, b) => (a || null) === (b || null),
  employeeIds: sameSet,
  capacity: same,
  price: (a, b) => (a || null) === (b || null),
  color: (a, b) => a.toLowerCase() === b.toLowerCase(),
  visibility: sameSet,
  description: (a, b) => a.trim() === b.trim(),
  cancellationDeadline: same,
  allowCustomerNote: same,
}

export function sameSeriesValue<F extends SeriesEditField>(
  field: F,
  a: SeriesEditValues,
  b: SeriesEditValues,
): boolean {
  return COMPARERS[field](a[field], b[field])
}

/** The fields `edited` changes against `opened`. Only these travel, so an untouched field is never written. */
export function seriesEditChanges(opened: SeriesEditValues, edited: SeriesEditValues): SeriesEditField[] {
  return SERIES_EDIT_FIELDS.filter((field) => !sameSeriesValue(field, opened, edited))
}

/**
 * What a participant is told about when an edit changes it (docs/events.md, _Telling participants_): what they see of
 * the termín they signed up for. Capacity, colour, visibility, the client-note toggle and the cancellation deadline (each
 * participation keeps the one it signed up with) are the premise's business.
 */
export const PARTICIPANT_FIELDS = [
  'name',
  'placeId',
  'employeeIds',
  'price',
] as const satisfies readonly SeriesEditField[]
export type ParticipantField = (typeof PARTICIPANT_FIELDS)[number]

/** The changed fields a participant is told about, in `PARTICIPANT_FIELDS` order. */
export function participantFields(fields: readonly SeriesEditField[]): ParticipantField[] {
  return PARTICIPANT_FIELDS.filter((field) => fields.includes(field))
}

/** The fields `after` changes against `before` that a participant is told about. */
export function participantChanges(before: SeriesEditValues, after: SeriesEditValues): ParticipantField[] {
  return participantFields(seriesEditChanges(before, after))
}

/**
 * Why the opened termín cannot start a series edit, worded as the API's refusal; `null` when it can. Only an upcoming
 * termín that stands can: a series edit never rewrites what is under way or over.
 */
export function seriesEditRefusal(
  source: EventTimes & { cancelledAt: Date | string | null },
  now: Date,
  timezone: string,
): 'event.cancelled' | 'event.ended' | 'event.started' | null {
  if (source.cancelledAt) {
    return 'event.cancelled'
  }
  const phase = eventPhase(source, now, timezone)
  if (phase === 'ongoing') {
    return 'event.started'
  }
  return phase === 'upcoming' ? null : 'event.ended'
}

/** Why a termín of the series is out of a series edit's reach. */
export type SeriesEditExclusion = 'cancelled' | 'started' | 'earlier'

export interface SeriesEditPlan {
  /**
   * The termíny the edit really changes, in time order with the opened one first, and the fields each gets - never a
   * field already holding the new value, never none. The set a change notification goes to.
   */
  targets: { id: string; fields: SeriesEditField[] }[]
  /** Per field in play, the later termíny in range holding their own value: neither the opened termín's nor the new. */
  differing: { [F in SeriesEditField]?: string[] }
  /** The later termíny in range that keep their own value in at least one field in play. */
  kept: string[]
  /** The other termíny of the series, which the edit does not touch, and why. */
  excluded: { id: string; reason: SeriesEditExclusion }[]
}

/**
 * Which termíny a series edit writes, and which fields each gets (docs/events.md, _Editing a series_).
 *
 * `next` holds the fields in play with their new values: the ones the manager changed, and the ones in `overwrite`
 * ("Zjednotiť" brings an unchanged field in, with the opened termín's own value). The range is the opened termín and
 * every later one that is upcoming and not cancelled. For each termín in range and each field in play:
 *
 * - holding the new value already - **no change**: not written, not counted;
 * - holding the opened termín's value, or the field is in `overwrite` - **written**;
 * - otherwise it was edited on its own in that field and **keeps its value**.
 *
 * Fields not in play are never written, so a termín's own values in them survive.
 *
 * `source` is assumed to pass `seriesEditRefusal`; it may or may not appear in `occurrences`.
 */
export function planSeriesEdit(input: {
  source: SeriesEditOccurrence
  occurrences: readonly SeriesEditOccurrence[]
  next: Partial<SeriesEditValues>
  overwrite: readonly SeriesEditField[]
  now: Date
  timezone: string
}): SeriesEditPlan {
  const { source, now, timezone } = input
  const overwrite = new Set(input.overwrite)
  const fields = SERIES_EDIT_FIELDS.filter((field) => input.next[field] !== undefined)
  const next: SeriesEditValues = { ...source }
  for (const field of fields) {
    Object.assign(next, { [field]: input.next[field] })
  }
  const changing = (values: SeriesEditValues) => fields.filter((field) => !sameSeriesValue(field, values, next))
  const from = eventPeriod(source, timezone)?.start
  const startOf = (occurrence: SeriesEditOccurrence) => eventPeriod(occurrence, timezone)?.start.toMillis() ?? 0

  const own = changing(source)
  const plan: SeriesEditPlan = {
    targets: own.length ? [{ id: source.id, fields: own }] : [],
    differing: {},
    kept: [],
    excluded: [],
  }
  const later = input.occurrences
    .filter((occurrence) => occurrence.id !== source.id)
    .sort((a, b) => startOf(a) - startOf(b))

  for (const occurrence of later) {
    const reason = exclusion(occurrence, from?.toMillis() ?? Infinity, now, timezone)
    if (reason) {
      plan.excluded.push({ id: occurrence.id, reason })
      continue
    }
    const changes = changing(occurrence)
    const ownValues = changes.filter((field) => !sameSeriesValue(field, occurrence, source))
    for (const field of ownValues) {
      plan.differing[field] = [...(plan.differing[field] ?? []), occurrence.id]
    }
    if (ownValues.some((field) => !overwrite.has(field))) {
      plan.kept.push(occurrence.id)
    }
    const written = changes.filter((field) => overwrite.has(field) || !ownValues.includes(field))
    if (written.length) {
      plan.targets.push({ id: occurrence.id, fields: written })
    }
  }
  return plan
}

/** A termín with no times at all counts as over: it cannot be placed after the opened one. */
function exclusion(
  occurrence: SeriesEditOccurrence,
  from: number,
  now: Date,
  timezone: string,
): SeriesEditExclusion | null {
  if (occurrence.cancelledAt) {
    return 'cancelled'
  }
  const period = eventPeriod(occurrence, timezone)
  if (!period || eventPhase(occurrence, now, timezone) !== 'upcoming') {
    return 'started'
  }
  return period.start.toMillis() < from ? 'earlier' : null
}
